import { List } from 'immutable';
import capitalize from 'just-capitalize';
import { extendMoment } from 'moment-range';
import Moment from 'moment-timezone';

import { MoonIcon, SunIcon, SunMoonIcon } from 'shared/components/ihcl/icon';
import { isTodayOrFuture } from 'shared/helpers/momentHelpers';

import {
  Interview,
  InterviewRequest,
  InterviewType,
  Showcase,
} from 'talent/types';

import ShowcasePresenter from './ShowcasePresenter';

const moment = extendMoment(Moment as any);

type InterviewRequestPresenterAttrs = {
  interviews?: Interview[];
  showcase?: Showcase;
};

class InterviewRequestPresenter {
  interviewRequest: InterviewRequest;

  interviews: Interview[];

  showcasePresenter: ShowcasePresenter;

  constructor(
    interviewRequest: InterviewRequest,
    { interviews, showcase }: InterviewRequestPresenterAttrs = {}
  ) {
    this.interviewRequest = interviewRequest;
    this.interviews = interviews;
    this.showcasePresenter = new ShowcasePresenter(showcase);
  }

  blockType() {
    const {
      status,
      application_links: applicationLinks,
      applied_at: appliedAt,
    } = this.interviewRequest;

    const hiringRoundType =
      this.interviewRequest.type === 'NurseInterviewRequest'
        ? this.interviewRequest.hiring_round_type
        : null;
    if (
      this.interviewRequest.interview_request_type === 'PendingInterviewRequest'
    ) {
      return this.isClosed() ? 'withdrawn' : 'pending';
    }

    if (this.isClosed()) {
      return 'closed';
    }

    if (
      status === 'requested' &&
      this.interviewRequest.ai_enabled &&
      ['in_progress', 'processing'].includes(
        this.interviewRequest.latest_async_interview_status
      )
    ) {
      return 'in_process';
    }

    if (hiringRoundType === 'fast_track' && status === 'requested') {
      return 'fast_track';
    }

    if (status === 'requested') {
      return 'accept_or_decline';
    }

    if (status === 'rejected') {
      return 'declined';
    }

    if (this.findUpcomingInterview('FollowUpInterview')) {
      return 'upcoming_follow_up_interview';
    }

    if (this.interviewRequest.post_accept_status === 'follow_up_requested') {
      return 'enter_availability';
    }

    if (
      this.interviewRequest.ai_enabled &&
      ['in_progress', 'processing'].includes(
        this.interviewRequest.latest_async_interview_status
      )
    ) {
      return 'in_process';
    }

    if (!this.hasPostAcceptStatus()) {
      if (this.interviewRequest.ai_enabled) {
        return 'ai_phone_screen';
      }
      return 'enter_availability';
    }

    if (this.interviewRequest.post_accept_status === 'offer_accepted') {
      return 'closed';
    }

    if (applicationLinks.length > 0 && [null, ''].includes(appliedAt)) {
      return 'fill_application';
    }

    if (this.findUpcomingInterview('PhoneScreenInterview')) {
      return 'upcoming_phone_screen_interview';
    }

    if (this.findUpcomingInterview('HiringManagerInterview')) {
      return 'upcoming_hiring_manager_interview';
    }

    return 'in_process';
  }

  section(hasFutureAvailability = false) {
    const blockType = this.blockType();

    const availabilityNeeded =
      !hasFutureAvailability ||
      this.interviewRequest['more_availability_request_not_responded_to?'];

    return {
      accept_or_decline: 'actionNeeded',
      ai_phone_screen: 'actionNeeded',
      closed: 'past',
      declined: 'past',
      enter_availability: availabilityNeeded ? 'actionNeeded' : 'upcoming',
      fast_track: 'actionNeeded',
      fill_application: 'actionNeeded',
      in_process: 'upcoming',
      pending: 'upcoming',
      upcoming_phone_screen_interview: 'upcoming',
      upcoming_hiring_manager_interview: 'upcoming',
      upcoming_follow_up_interview: 'upcoming',
      withdrawn: 'past',
    }[blockType];
  }

  hasPostAcceptStatus() {
    return this.interviewRequest.has_post_accept_status;
  }

  isClosed() {
    return this.interviewRequest.is_closed;
  }

  previouslyRescheduled() {
    return this.interviews.some(
      (i) =>
        i.interview_request_id === this.interviewRequest.id &&
        i.status === 'rescheduled'
    );
  }

  findUpcomingInterview(type: InterviewType) {
    const interviewRequestId = this.interviewRequest.id;

    return this.interviews.find((i) => {
      const matches = i.interview_request_id === interviewRequestId;
      const isInterviewType = i.type === type;
      const isScheduled = i.status === 'scheduled';
      const scheduledAtMoment = moment(i.scheduled_at);
      const isUpcoming = isTodayOrFuture(scheduledAtMoment);
      return matches && isInterviewType && isScheduled && isUpcoming;
    });
  }

  locations() {
    const { interview_request_locations: locations } = this.interviewRequest;

    return locations
      .map((location) => {
        const locationInfo = [
          location.neighborhood,
          location.city,
          location.state,
        ];
        return locationInfo.filter(Boolean).join(' ');
      })
      .join(', ');
  }

  groupedLocations() {
    return List(this.interviewRequest.interview_request_locations)
      .groupBy((irl: any) => irl.state.toUpperCase())
      .entrySeq()
      .map(([state, locs]) => {
        const locsByCity = locs.groupBy((loc) => loc.city);
        const cities = locsByCity
          .entrySeq()
          .map(([city, facilities]) => {
            if (facilities.count() > 1) {
              return `${city} (${facilities.count()} facilities)`;
            }
            return city;
          })
          .join(', ');
        return `${cities}, ${state.toUpperCase()}`;
      })
      .join('; ');
  }

  employerHighlights() {
    return this.showcasePresenter.employerHighlights();
  }

  benefitHighlights() {
    return this.showcasePresenter.benefitHighlights();
  }

  modifiedShowcaseMessage() {
    const { employer_group_name: employerGroupName } = this.interviewRequest;
    return this.showcasePresenter.modifiedShowcaseMessage(employerGroupName);
  }

  employmentTypesText() {
    const { employment_types: employmentTypes } = this.interviewRequest;
    if (!employmentTypes?.length) {
      return null;
    }

    const fullTime = employmentTypes.includes('full_time');
    const partTime = employmentTypes.includes('part_time');
    const perDiem = employmentTypes.includes('per_diem');

    if (fullTime && partTime && perDiem) {
      return 'FT, PT, Per Diem';
    }

    if (fullTime && partTime) {
      return 'Full Time, Part Time';
    }

    const titleCase = {
      full_time: 'Full Time',
      part_time: 'Part Time',
      per_diem: 'Per Diem',
    };

    return employmentTypes
      .sort()
      .map((employmentType) => titleCase[employmentType])
      .join(', ');
  }

  shiftsText() {
    if (!this.interviewRequest.shifts?.length) {
      return null;
    }

    return this.interviewRequest.shifts
      .map((shift) => capitalize(shift.replace('_shift', '')))
      .join(', ')
      .concat(' Shift');
  }

  shiftsIcon() {
    const { shifts } = this.interviewRequest;
    const hasSun =
      shifts?.includes('day_shift') || shifts?.includes('mid_shift');
    const hasMoon = shifts?.includes('night_shift');
    if (hasSun && !hasMoon) {
      return SunIcon;
    }
    if (hasMoon && !hasSun) {
      return MoonIcon;
    }

    return SunMoonIcon;
  }
}

export default InterviewRequestPresenter;
